






















































































































































import {
  defineComponent,
  PropType,
  ref,
  computed,
  ComputedRef,
} from '@vue/composition-api';
import {
  EpigraphicWord,
  EpigraphicUnitType,
  MarkupType,
  EditorDiscourseWord,
  SplitWordPayload,
  SearchSpellingResultRow,
} from '@oare/types';
import ConnectDiscourseItem from '@/views/Texts/CollectionTexts/AddTexts/Discourse/components/ConnectDiscourseItem.vue';
import sl from '@/serviceLocator';

export default defineComponent({
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    word: {
      type: Object as PropType<EpigraphicWord>,
      required: true,
    },
    textUuid: {
      type: String,
      required: true,
    },
  },
  components: {
    ConnectDiscourseItem,
  },
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const splitWordLoading = ref(false);

    const step = ref(1);

    const formsLoaded = ref(false);
    const formsLoaded2 = ref(false);

    const splitWord = async () => {
      try {
        splitWordLoading.value = true;

        if (splitIndex.value === undefined) {
          throw new Error('Split index is undefined');
        }

        const newSpellings = getUpdatedSignsWithSeparators();
        if (newSpellings.length !== 2) {
          throw new Error('New spellings length is not 2');
        }

        if (!props.word.discourseUuid) {
          throw new Error('No discourse UUID');
        }

        if (propertySelections.value.length > 2) {
          throw new Error('Too many property selections');
        }

        const previousUuid = props.word.signs[splitIndex.value].uuid;

        const payload: SplitWordPayload = {
          type: 'splitWord',
          textUuid: props.textUuid,
          discourseUuid: props.word.discourseUuid,
          firstSpelling: newSpellings[0],
          firstSpellingUuid: form.value ? form.value.spellingUuid : null,
          firstTranscription: form.value ? form.value.form.form : null,
          secondSpelling: newSpellings[1],
          secondSpellingUuid: form2.value ? form2.value.spellingUuid : null,
          secondTranscription: form2.value ? form2.value.form.form : null,
          previousUuid,
          propertySelections: propertySelections.value,
        };

        await server.editText(payload);
        emit('reset-renderer');
      } catch (err) {
        actions.showErrorSnackbar(
          'Error splitting word/number. Please try again.',
          err as Error
        );
      } finally {
        emit('input', false);
        emit('reset-current-edit-action');
        splitWordLoading.value = false;
      }
    };

    const goBack = () => {
      step.value -= 1;
      if (step.value === 1) {
        form.value = undefined;
        form2.value = undefined;
        formsLoaded.value = false;
        formsLoaded2.value = false;
      } else if (step.value === 2) {
        propertySelections.value = [0];
      }
    };

    const form = ref<SearchSpellingResultRow>();
    const form2 = ref<SearchSpellingResultRow>();

    const splitIndex = ref<number>();

    const getUpdatedSignsWithSeparators = (): string[] => {
      if (splitIndex.value === undefined) {
        return [];
      }

      const pieces: {
        uuid: string;
        reading: string;
        type: EpigraphicUnitType | null;
        markup: MarkupType[];
      }[] = props.word.signs.map(sign => ({
        uuid: sign.uuid,
        reading: sign.reading || '',
        type: sign.type,
        markup: sign.markups.map(unit => unit.type),
      }));

      const firstWordPieces = pieces.slice(0, splitIndex.value + 1);
      const secondWordPieces = pieces.slice(splitIndex.value + 1);

      const newWords = [firstWordPieces, secondWordPieces].map(pieces =>
        pieces.map((sign, index) => {
          const nextSign =
            index !== pieces.length - 1 ? pieces[index + 1] : null;

          let newSeparator = '';
          if (nextSign) {
            if (
              !sign.markup.includes('phoneticComplement') &&
              nextSign.markup.includes('phoneticComplement')
            ) {
              newSeparator = '';
            } else if (
              sign.type === 'determinative' ||
              nextSign.type === 'determinative'
            ) {
              newSeparator = '';
            } else if (
              sign.type === 'phonogram' ||
              nextSign.type === 'phonogram'
            ) {
              newSeparator = '-';
            } else if (sign.type === 'number' && nextSign.type === 'number') {
              newSeparator = '+';
            } else if (
              sign.type === 'logogram' ||
              nextSign.type === 'logogram'
            ) {
              newSeparator = '.';
            }
          }

          let newReading = sign.reading;
          if (sign.type === 'determinative') {
            newReading = `(${newReading})`;
          }

          return {
            ...sign,
            reading: newReading,
            separator: newSeparator,
          };
        })
      );

      const newWordsReading = newWords.map(word => {
        let newWordReading = '';
        word.forEach(sign => {
          newWordReading += sign.reading;
          if (sign.separator) {
            newWordReading += sign.separator;
          }
        });
        return newWordReading
          .replace(/([[\]{}⸢⸣«»‹›:;*?\\!])|(".+")|('.+')|(^\/)+/g, '')
          .replace(/<[^>]*>/g, '');
      });

      return newWordsReading;
    };

    const editorDiscourseWord1: ComputedRef<EditorDiscourseWord | undefined> =
      computed(() => {
        if (splitIndex.value === undefined) {
          return undefined;
        }
        const newWord = getUpdatedSignsWithSeparators()[0];
        return {
          discourseUuid: props.word.discourseUuid,
          spelling: newWord,
          type: 'word',
        };
      });

    const editorDiscourseWord2: ComputedRef<EditorDiscourseWord | undefined> =
      computed(() => {
        if (splitIndex.value === undefined) {
          return undefined;
        }
        const newWord = getUpdatedSignsWithSeparators()[1];
        return {
          discourseUuid: props.word.discourseUuid,
          spelling: newWord,
          type: 'word',
        };
      });

    const submitDisabled = computed(() => {
      if (step.value === 1) {
        return splitIndex.value === undefined;
      }
      if (step.value === 2) {
        return !formsLoaded.value || !formsLoaded2.value;
      }
      return false;
    });

    const propertySelections = ref<number[]>([0]);

    return {
      splitWordLoading,
      step,
      formsLoaded,
      formsLoaded2,
      splitWord,
      goBack,
      form,
      splitIndex,
      form2,
      getUpdatedSignsWithSeparators,
      editorDiscourseWord1,
      editorDiscourseWord2,
      submitDisabled,
      propertySelections,
    };
  },
});
